/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Loader2, Mail, Check, Plus, Trash2, Settings2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_URL, USER_ROLES } from '@/constant/constant';
import { useUser } from '@/contexts/UserContext';
import { useCategoryIcons } from '@/contexts/CategoryIconContext';
import BiglocalLoader from '../../../components/common/BigLocalLoader';
import BottomNav from '../../../components/layouts/Footer/BottomNav';
import emailjs from '@emailjs/browser';
import axiosInstance from '../../../utils/axiosInstance';
import { inventoryApi } from '../../vendor/api/InventoryApi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// --- Helper Function for Grid View ---

const isSelectableNode = (node) =>
  node.selectable || !node.subCategories || node.subCategories.length === 0;

const groupLeafNodesByRoot = (categories) => {
  if (!categories) return [];
  const grouped = [];
  for (const rootCategory of categories) {
    const leafNodes = [];
    const findLeafs = (category) => {
      if (isSelectableNode(category)) {
        leafNodes.push(category);
      }
      (category.subCategories || []).forEach(findLeafs);
    };
    findLeafs(rootCategory);
    if (leafNodes.length > 0) {
      grouped.push({
        id: rootCategory.id,
        name: rootCategory.name,
        leafNodes: leafNodes,
      });
    }
  }
  return grouped;
};

const extractLeafIds = (categories) => {
  if (!categories) return [];
  const leafIds = [];
  const traverse = (cats) => {
    for (const cat of cats) {
      if (isSelectableNode(cat)) {
        leafIds.push(cat.id);
      }
      traverse(cat.subCategories || []);
    }
  };
  traverse(categories);
  return leafIds;
};

const hasSelectedDescendants = (node, selectedSet) =>
  (node.subCategories || []).some(
    (sub) => selectedSet.has(sub.id) || hasSelectedDescendants(sub, selectedSet),
  );

// Selected nodes that have no selected descendants (matches VendorDashboard leaf logic)
const getEffectiveLeafIds = (categories, selectedSet) => {
  const ids = [];
  const traverse = (nodes) => {
    for (const node of nodes) {
      if (selectedSet.has(node.id) && !hasSelectedDescendants(node, selectedSet)) {
        ids.push(node.id);
      }
      traverse(node.subCategories || []);
    }
  };
  traverse(categories);
  return ids;
};

const collectCategorySettings = (nodes, settings = {}) => {
  nodes.forEach((node) => {
    if (isSelectableNode(node)) {
      settings[node.id] = {
        categoryType: node.categoryType || 'SHOP',
        activated: node.activated !== false,
        priority: node.priority ?? 0,
        eventDate: node.eventDate || '',
        eventBannerUrl: node.eventBannerUrl || '',
        eventVenue: node.eventVenue || '',
        eventVenueLink: node.eventVenueLink || '',
        runCategory: node.runCategory || '',
        tshirtSize: node.tshirtSize || '',
        eventDetails: node.eventDetails || '',
        eventImageUrls: node.eventImageUrls || [],
        taxMode: node.taxMode || 'EXCLUSIVE',
        taxApplicableTo: node.taxApplicableTo || 'ALL',
        taxInclusiveProductIdsText:
          (node.taxInclusiveProductIds || []).join(', '),
        questions: (node.questions || []).map((q) => ({
          id: q.id || '',
          questionKey: q.questionKey || '',
          questionText: q.questionText || '',
          required: q.required ?? false,
          inputType: q.inputType || 'TEXT',
          options: q.options || '',
          displayOrder: q.displayOrder ?? 0,
          applicableProductIdsText:
            (q.applicableProductIds || []).join(', '),
        })),
      };
    }
    collectCategorySettings(node.subCategories || [], settings);
  });
  return settings;
};

// --- Main Component: CategoriesEdit ---

const CategoriesEdit = () => {
  const [allCategories, setAllCategories] = useState([]);
  const [selectedLeafNodes, setSelectedLeafNodes] = useState(new Set());
  const [categorySettings, setCategorySettings] = useState({});
  const [categoryProducts, setCategoryProducts] = useState({});
  const [configureCategoryId, setConfigureCategoryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useUser();
  const navigate = useNavigate();
  const getIconUrl = useCategoryIcons();

  useEffect(() => {
    if (user && user.vendorCategories) {
      setAllCategories(user.vendorCategories);
      const initialSelectedIds = extractLeafIds(user.vendorCategories);
      setSelectedLeafNodes(new Set(initialSelectedIds));
      setCategorySettings(collectCategorySettings(user.vendorCategories));
      setLoading(false);
    } else if (user) {
      setAllCategories([]);
      setSelectedLeafNodes(new Set());
      setCategorySettings({});
      setLoading(false);
    }
  }, [user]);

  const handleUpdateCategories = async () => {
    const leafNodeIdArray = getEffectiveLeafIds(allCategories, selectedLeafNodes);

    if (leafNodeIdArray.length === 0) {
      toast.error('Please select at least one category.');
      return;
    }

    const hasInvalidPriority = leafNodeIdArray.some((categoryId, index) =>
      !/^\d+$/.test(String(categorySettings[categoryId]?.priority ?? index)),
    );
    if (hasInvalidPriority) {
      toast.error('Priority must contain only non-negative whole numbers');
      return;
    }
    if (
      leafNodeIdArray.some(
        (categoryId) =>
          categorySettings[categoryId]?.categoryType === 'EVENT' &&
          !categorySettings[categoryId]?.eventDate,
      )
    ) {
      toast.error('Select an event date for every event category');
      return;
    }
    const hasInvalidEventUrl = leafNodeIdArray.some((categoryId) => {
      const setting = categorySettings[categoryId];
      if (setting?.categoryType !== 'EVENT') return false;
      const urls = [setting.eventBannerUrl, ...(setting.eventImageUrls || [])]
        .map((url) => (url || '').trim())
        .filter(Boolean);
      return urls.some((url) => !url.startsWith('https://'));
    });
    if (hasInvalidEventUrl) {
      toast.error('Event banner and image URLs must be valid https URLs');
      return;
    }
    const hasInvalidEventVenueLink = leafNodeIdArray.some((categoryId) => {
      const setting = categorySettings[categoryId];
      if (setting?.categoryType !== 'EVENT') return false;
      const url = (setting.eventVenueLink || '').trim();
      return url && !url.startsWith('https://');
    });
    if (hasInvalidEventVenueLink) {
      toast.error('Event venue link must be a valid https URL');
      return;
    }

    setUpdateLoading(true);
    try {
      const sessionToken = localStorage.getItem('sessionToken');
      const assignments = leafNodeIdArray.map((categoryId, index) => {
        const setting = categorySettings[categoryId] || {};
        const isEvent = setting.categoryType === 'EVENT';
        const taxInclusiveProductIds = (setting.taxInclusiveProductIdsText || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
        return {
          categoryId,
          categoryType: setting.categoryType || 'SHOP',
          activated: setting.activated ?? true,
          priority: Number(setting.priority ?? index),
          eventDate:
            isEvent && setting.eventDate ? `${setting.eventDate}T00:00:00` : null,
          eventBannerUrl: isEvent ? setting.eventBannerUrl || null : null,
          eventVenue: isEvent ? setting.eventVenue || null : null,
          eventVenueLink: isEvent ? setting.eventVenueLink || null : null,
          runCategory: isEvent ? setting.runCategory || null : null,
          tshirtSize: isEvent ? setting.tshirtSize || null : null,
          eventDetails: isEvent ? setting.eventDetails || null : null,
          eventImageUrls: isEvent
            ? (setting.eventImageUrls || [])
                .map((url) => (url || '').trim())
                .filter(Boolean)
            : [],
          taxMode: setting.taxMode || 'EXCLUSIVE',
          taxApplicableTo: setting.taxApplicableTo || 'ALL',
          taxInclusiveProductIds:
            setting.taxMode === 'INCLUSIVE' &&
            setting.taxApplicableTo === 'SPECIFIC'
              ? taxInclusiveProductIds
              : [],
          questions: (setting.questions || []).map((q) => ({
            id: q.id || undefined,
            questionKey: q.questionKey || '',
            questionText: q.questionText || '',
            required: q.required ?? false,
            inputType: q.inputType || 'TEXT',
            options: q.options || null,
            displayOrder: Number(q.displayOrder ?? 0),
            applicableProductIds: (q.applicableProductIdsText || '')
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean),
          })),
        };
      });
      await axiosInstance.put(
        `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/categories/v2`,
        assignments,
        {
          headers: {
            'X-Vendor-Id': user?.id,
            sessionToken,
            'X-USER-ROLE': USER_ROLES.VENDOR,
          },
        }
      );
      toast.success('Categories updated successfully!');
      navigate(-1);
    } catch (error) {
      console.error(error);
      toast.error('Failed to update categories.');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleToggleLeafNode = useCallback((nodeId) => {
    setSelectedLeafNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const updateCategorySetting = (categoryId, field, value) => {
    setCategorySettings((current) => ({
      ...current,
      [categoryId]: {
        categoryType: 'SHOP',
        activated: true,
        priority: 0,
        eventDate: '',
        eventBannerUrl: '',
        eventVenue: '',
        eventVenueLink: '',
        runCategory: '',
        tshirtSize: '',
        eventDetails: '',
        eventImageUrls: [],
        taxMode: 'EXCLUSIVE',
        taxApplicableTo: 'ALL',
        taxInclusiveProductIdsText: '',
        questions: [],
        ...current[categoryId],
        [field]: value,
      },
    }));
  };

  const addQuestion = (categoryId) => {
    setCategorySettings((current) => ({
      ...current,
      [categoryId]: {
        ...current[categoryId],
        questions: [
          ...(current[categoryId]?.questions || []),
          {
            id: '',
            questionKey: '',
            questionText: '',
            required: false,
            inputType: 'TEXT',
            options: '',
            displayOrder: (current[categoryId]?.questions || []).length,
            applicableProductIdsText: '',
          },
        ],
      },
    }));
  };

  const removeQuestion = (categoryId, questionIndex) => {
    setCategorySettings((current) => ({
      ...current,
      [categoryId]: {
        ...current[categoryId],
        questions: (current[categoryId]?.questions || []).filter(
          (_, index) => index !== questionIndex
        ),
      },
    }));
  };

  const updateQuestion = (categoryId, questionIndex, field, value) => {
    setCategorySettings((current) => ({
      ...current,
      [categoryId]: {
        ...current[categoryId],
        questions: (current[categoryId]?.questions || []).map((q, index) =>
          index === questionIndex ? { ...q, [field]: value } : q
        ),
      },
    }));
  };

  const fetchCategoryProducts = async (categoryId) => {
    if (!user?.id || categoryProducts[categoryId] !== undefined) return;
    const result = await inventoryApi.fetchProductsByCategory(
      user.id,
      categoryId,
      categoryId,
    );
    if (result.success) {
      setCategoryProducts((prev) => ({
        ...prev,
        [categoryId]: result.data || [],
      }));
    } else {
      setCategoryProducts((prev) => ({ ...prev, [categoryId]: [] }));
      toast.error(`Failed to load products for category`);
    }
  };

  const toggleCategoryProductId = (categoryId, field, productId) => {
    const current = categorySettings[categoryId]?.[field] || '';
    const ids = current
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const idSet = new Set(ids);
    if (idSet.has(productId)) {
      idSet.delete(productId);
    } else {
      idSet.add(productId);
    }
    updateCategorySetting(categoryId, field, Array.from(idSet).join(', '));
  };

  const toggleQuestionProductId = (categoryId, questionIndex, productId) => {
    const current =
      categorySettings[categoryId]?.questions?.[questionIndex]
        ?.applicableProductIdsText || '';
    const ids = current
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    const idSet = new Set(ids);
    if (idSet.has(productId)) {
      idSet.delete(productId);
    } else {
      idSet.add(productId);
    }
    updateQuestion(
      categoryId,
      questionIndex,
      'applicableProductIdsText',
      Array.from(idSet).join(', '),
    );
  };

  const configuredCategory = useMemo(
    () => findCategoryInTree(allCategories, configureCategoryId),
    [allCategories, configureCategoryId],
  );

  const groupedCategories = useMemo(() => groupLeafNodesByRoot(allCategories), [allCategories]);

  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return groupedCategories;
    const result = [];
    for (const group of groupedCategories) {
      const matchingLeafs = group.leafNodes.filter((leaf) =>
        leaf.name.toLowerCase().includes(query)
      );
      if (group.name.toLowerCase().includes(query) || matchingLeafs.length > 0) {
        result.push({
          ...group,
          leafNodes: group.name.toLowerCase().includes(query) ? group.leafNodes : matchingLeafs,
        });
      }
    }
    return result;
  }, [searchQuery, groupedCategories]);

  if (loading) return <BiglocalLoader title="Loading your categories..." />;

  return (
    <div className="min-h-screen w-full bg-slate-100 flex flex-col">
      <div className="flex-grow w-full max-w-8xl mx-auto  py-4">
        <div className="px-4">
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Manage Your Categories</h1>
        </div>

        <div className="flex px-4 items-center mb-4 gap-2 sticky top-[65px] bg-slate-100 py-2 z-10">
          <div className="relative flex-grow">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              type="text"
              placeholder="Search for any category or item..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-11 text-base border-slate-300 focus:border-green-600 rounded-md w-full"
            />
          </div>
          <Button
            onClick={handleUpdateCategories}
            disabled={updateLoading || selectedLeafNodes.size === 0}
            className="h-11 bg-green-700 text-white hover:bg-green-800 rounded-md px-4 font-semibold min-w-[100px]"
          >
            {updateLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Save'}
          </Button>
          <Button
            onClick={() => setIsEmailFormOpen(true)}
            variant="outline"
            className="h-11 bg-orange-500 text-white hover:bg-orange-600 hover:text-white rounded-md px-4"
          >
            Request
          </Button>
        </div>

        <main className="pb-36 px-4">
          <div className="space-y-3">
            {filteredData.length > 0 ? (
              filteredData.map((group) => (
                <section key={group.id}>
                  <h2 className="text-xl font-bold text-slate-900 border-b-2 border-slate-200 pb-2 mb-4">
                    {group.name}
                  </h2>
                  <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2">
                    {group.leafNodes.map((leaf) => {
                      const isSelected = selectedLeafNodes.has(leaf.id);
                      return (
                        <div
                          key={leaf.id}
                          onClick={() => handleToggleLeafNode(leaf.id)}
                          className={`relative justify-between overflow-hidden cursor-pointer rounded-lg flex flex-col items-center text-center transition-all duration-200 bg-white ${isSelected ? 'border-2 border-green-500 shadow-md scale-105' : 'border border-slate-200 hover:shadow-lg hover:border-slate-400'}`}
                        >
                          {isSelected && (
                            <div className="absolute top-0 right-0 w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white border-2 border-white shadow-lg">
                              <Check className="w-4 h-4" strokeWidth={3} />
                            </div>
                          )}
                          {isSelected && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchCategoryProducts(leaf.id);
                                setConfigureCategoryId(leaf.id);
                              }}
                              className="absolute top-0 left-0 w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center text-white border-2 border-white shadow-lg hover:bg-slate-700"
                              aria-label={`Configure ${leaf.name}`}
                            >
                              <Settings2 className="w-3 h-3" />
                            </button>
                          )}
                          <img
                            src={getIconUrl(leaf.name)}
                            alt={leaf.name}
                            onError={(e) => {
                              e.currentTarget.src = '/images/imagenotfound.webp';
                            }}
                            className="w-full h-auto object-contain mb-1"
                          />
                          <div className="w-full mt-auto px-1 pb-3">
                            <h4
                              className="font-semibold break-words line-clamp-1
                             text-xs text-slate-800 leading-tight"
                            >
                              {leaf.name}
                            </h4>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            ) : (
              <div className="text-center py-12">
                <h3 className="text-xl font-bold text-slate-900 mb-2">No Categories Found</h3>
                <p className="text-slate-600">
                  Your search for &quot;{searchQuery}&quot; did not match any categories.
                </p>
              </div>
            )}
          </div>
        </main>

        <div className="fixed bottom-16 md:bottom-0 w-full left-0 right-0 px-4 py-3 bg-white border-t z-20 shadow-lg">
          <div className="max-w-8xl mx-auto flex justify-end">
            <Button
              onClick={handleUpdateCategories}
              disabled={updateLoading || selectedLeafNodes.size === 0}
              className="w-full md:w-fit px-4 h-10 text-sm bg-green-700 hover:bg-green-800 font-bold rounded-md text-white"
            >
              {updateLoading ? (
                <Loader2 className="w-5 h-5 animate-spin mx-auto" />
              ) : (
                `Save Categories (${selectedLeafNodes.size})`
              )}
            </Button>
          </div>
        </div>
        <BottomNav page="categories" />
      </div>
      <Dialog
        open={!!configureCategoryId}
        onOpenChange={(open) => {
          if (!open) setConfigureCategoryId(null);
        }}
      >
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Configure {configuredCategory?.name || 'Category'}
            </DialogTitle>
          </DialogHeader>
          {configureCategoryId && (
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
                  <input
                    type="checkbox"
                    checked={
                      categorySettings[configureCategoryId]?.activated ?? true
                    }
                    onChange={(e) =>
                      updateCategorySetting(
                        configureCategoryId,
                        'activated',
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-300 text-green-600"
                  />
                  Active
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1 text-xs font-medium text-slate-600">
                  Type
                  <select
                    value={
                      categorySettings[configureCategoryId]?.categoryType ||
                      'SHOP'
                    }
                    onChange={(e) =>
                      updateCategorySetting(
                        configureCategoryId,
                        'categoryType',
                        e.target.value,
                      )
                    }
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900"
                  >
                    <option value="SHOP">Shop</option>
                    <option value="EVENT">Event</option>
                    <option value="SERVICES">Services</option>
                  </select>
                </label>
                <label className="space-y-1 text-xs font-medium text-slate-600">
                  Priority
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={
                      categorySettings[configureCategoryId]?.priority ?? 0
                    }
                    onChange={(e) =>
                      updateCategorySetting(
                        configureCategoryId,
                        'priority',
                        e.target.value.replace(/\D/g, ''),
                      )
                    }
                    className="h-10 bg-white"
                  />
                </label>
                <label className="space-y-1 text-xs font-medium text-slate-600">
                  Tax mode
                  <select
                    value={
                      categorySettings[configureCategoryId]?.taxMode ||
                      'EXCLUSIVE'
                    }
                    onChange={(e) =>
                      updateCategorySetting(
                        configureCategoryId,
                        'taxMode',
                        e.target.value,
                      )
                    }
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900"
                  >
                    <option value="EXCLUSIVE">Exclusive</option>
                    <option value="INCLUSIVE">Inclusive</option>
                  </select>
                </label>
                <label className="space-y-1 text-xs font-medium text-slate-600">
                  Tax applies to
                  <select
                    value={
                      categorySettings[configureCategoryId]?.taxApplicableTo ||
                      'ALL'
                    }
                    disabled={
                      categorySettings[configureCategoryId]?.taxMode !==
                      'INCLUSIVE'
                    }
                    onChange={(e) =>
                      updateCategorySetting(
                        configureCategoryId,
                        'taxApplicableTo',
                        e.target.value,
                      )
                    }
                    className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 disabled:bg-slate-100"
                  >
                    <option value="ALL">All products</option>
                    <option value="SPECIFIC">Specific products</option>
                  </select>
                </label>
              </div>
              {categorySettings[configureCategoryId]?.taxMode === 'INCLUSIVE' &&
                categorySettings[configureCategoryId]?.taxApplicableTo ===
                  'SPECIFIC' && (
                  <label className="space-y-2 text-xs font-medium text-slate-600">
                    Tax-inclusive products
                    <div className="grid gap-2 max-h-40 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                      {categoryProducts[configureCategoryId]?.length > 0 ? (
                        categoryProducts[configureCategoryId].map((product) => {
                          const selected = (
                            categorySettings[configureCategoryId]
                              ?.taxInclusiveProductIdsText || ''
                          )
                            .split(',')
                            .map((id) => id.trim())
                            .filter(Boolean)
                            .includes(product.productId);
                          return (
                            <label
                              key={product.productId}
                              className="flex items-center gap-2 text-xs text-slate-700"
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() =>
                                  toggleCategoryProductId(
                                    configureCategoryId,
                                    'taxInclusiveProductIdsText',
                                    product.productId,
                                  )
                                }
                                className="w-4 h-4 rounded border-slate-300 text-green-600"
                              />
                              {product.productName}
                            </label>
                          );
                        })
                      ) : (
                        <span className="text-xs text-slate-400">
                          No products available.
                        </span>
                      )}
                    </div>
                  </label>
                )}
              {categorySettings[configureCategoryId]?.categoryType ===
                'EVENT' && (
                <div className="grid grid-cols-2 gap-3">
                  <label className="space-y-1 text-xs font-medium text-slate-600">
                    Event date *
                    <Input
                      type="date"
                      required
                      value={
                        categorySettings[configureCategoryId]?.eventDate || ''
                      }
                      onChange={(e) =>
                        updateCategorySetting(
                          configureCategoryId,
                          'eventDate',
                          e.target.value,
                        )
                      }
                      className="h-10 bg-white"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-medium text-slate-600">
                    Event venue
                    <Input
                      type="text"
                      placeholder="e.g., Max Super Speciality, Dwarka"
                      value={
                        categorySettings[configureCategoryId]?.eventVenue || ''
                      }
                      onChange={(e) =>
                        updateCategorySetting(
                          configureCategoryId,
                          'eventVenue',
                          e.target.value,
                        )
                      }
                      className="h-10 bg-white"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-medium text-slate-600 col-span-2">
                    Event venue link
                    <Input
                      type="url"
                      placeholder="https://maps.app.goo.gl/..."
                      value={
                        categorySettings[configureCategoryId]?.eventVenueLink ||
                        ''
                      }
                      onChange={(e) =>
                        updateCategorySetting(
                          configureCategoryId,
                          'eventVenueLink',
                          e.target.value,
                        )
                      }
                      className="h-10 bg-white"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-medium text-slate-600">
                    Run categories
                    <Input
                      type="text"
                      placeholder="e.g., 21K, 15K, 10K, 5K"
                      value={
                        categorySettings[configureCategoryId]?.runCategory || ''
                      }
                      onChange={(e) =>
                        updateCategorySetting(
                          configureCategoryId,
                          'runCategory',
                          e.target.value,
                        )
                      }
                      className="h-10 bg-white"
                    />
                  </label>
                  <label className="space-y-1 text-xs font-medium text-slate-600">
                    T-shirt sizes
                    <Input
                      type="text"
                      placeholder="e.g., XS, S, M, L, XL, XXL"
                      value={
                        categorySettings[configureCategoryId]?.tshirtSize || ''
                      }
                      onChange={(e) =>
                        updateCategorySetting(
                          configureCategoryId,
                          'tshirtSize',
                          e.target.value,
                        )
                      }
                      className="h-10 bg-white"
                    />
                  </label>
                  <div className="space-y-1 text-xs font-medium text-slate-600 col-span-2">
                    Event details
                    <div className="rounded-md border border-slate-300 bg-white">
                      <ReactQuill
                        theme="snow"
                        value={
                          categorySettings[configureCategoryId]?.eventDetails ||
                          ''
                        }
                        onChange={(value) =>
                          updateCategorySetting(
                            configureCategoryId,
                            'eventDetails',
                            value,
                          )
                        }
                        placeholder="Enter about the event..."
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            [{ align: [] }],
                            ['link'],
                            ['clean'],
                          ],
                        }}
                        formats={[
                          'header',
                          'bold',
                          'italic',
                          'underline',
                          'strike',
                          'list',
                          'bullet',
                          'align',
                          'link',
                        ]}
                        className="rich-text-editor"
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600">
                    Dynamic questions
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addQuestion(configureCategoryId)}
                    className="text-xs"
                  >
                    <Plus className="mr-1 w-3 h-3" /> Add question
                  </Button>
                </div>
                {(categorySettings[configureCategoryId]?.questions || []).map(
                  (question, qIndex) => (
                    <div
                      key={qIndex}
                      className="rounded-md border border-slate-200 bg-white p-2 space-y-2"
                    >
                      <p className="text-[10px] text-slate-500">
                        Question key is a unique identifier used in participant
                        answers (e.g. tshirt_size, emergency_contact).
                      </p>
                      <div className="grid gap-2 grid-cols-2">
                        <Input
                          placeholder="Question key"
                          value={question.questionKey || ''}
                          onChange={(e) =>
                            updateQuestion(
                              configureCategoryId,
                              qIndex,
                              'questionKey',
                              e.target.value,
                            )
                          }
                          className="h-9"
                        />
                        <Input
                          placeholder="Question text"
                          value={question.questionText || ''}
                          onChange={(e) =>
                            updateQuestion(
                              configureCategoryId,
                              qIndex,
                              'questionText',
                              e.target.value,
                            )
                          }
                          className="h-9"
                        />
                        <select
                          value={question.inputType || 'TEXT'}
                          onChange={(e) =>
                            updateQuestion(
                              configureCategoryId,
                              qIndex,
                              'inputType',
                              e.target.value,
                            )
                          }
                          className="h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900"
                        >
                          <option value="TEXT">Text</option>
                          <option value="NUMBER">Number</option>
                          <option value="DATE">Date</option>
                          <option value="SELECT">Select</option>
                          <option value="BOOLEAN">Boolean</option>
                        </select>
                        <Input
                          type="text"
                          placeholder="Options (comma separated)"
                          value={question.options || ''}
                          onChange={(e) =>
                            updateQuestion(
                              configureCategoryId,
                              qIndex,
                              'options',
                              e.target.value,
                            )
                          }
                          className="h-9"
                        />
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="Display order"
                          value={question.displayOrder ?? 0}
                          onChange={(e) =>
                            updateQuestion(
                              configureCategoryId,
                              qIndex,
                              'displayOrder',
                              e.target.value.replace(/\D/g, ''),
                            )
                          }
                          className="h-9"
                        />
                        <div className="space-y-1">
                          <span className="text-[10px] text-slate-500">
                            Applicable products
                          </span>
                          <div className="grid gap-2 max-h-32 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                            {categoryProducts[configureCategoryId]?.length > 0 ? (
                              categoryProducts[configureCategoryId].map((product) => {
                                const selected = (question.applicableProductIdsText || '')
                                  .split(',')
                                  .map((id) => id.trim())
                                  .filter(Boolean)
                                  .includes(product.productId);
                                return (
                                  <label
                                    key={product.productId}
                                    className="flex items-center gap-2 text-xs text-slate-700"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={() =>
                                        toggleQuestionProductId(
                                          configureCategoryId,
                                          qIndex,
                                          product.productId,
                                        )
                                      }
                                      className="w-4 h-4 rounded border-slate-300 text-green-600"
                                    />
                                    {product.productName}
                                  </label>
                                );
                              })
                            ) : (
                              <span className="text-xs text-slate-400">
                                No products available.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={question.required || false}
                            onChange={(e) =>
                              updateQuestion(
                                configureCategoryId,
                                qIndex,
                                'required',
                                e.target.checked,
                              )
                            }
                            className="w-4 h-4 rounded border-slate-300 text-green-600"
                          />
                          Required
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            removeQuestion(configureCategoryId, qIndex)
                          }
                          className="text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </Button>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <EmailForm isOpen={isEmailFormOpen} onClose={() => setIsEmailFormOpen(false)} />
    </div>
  );
};

const findCategoryInTree = (categories, id) => {
  for (const category of categories || []) {
    if (category.id === id) return category;
    const found = findCategoryInTree(category.subCategories, id);
    if (found) return found;
  }
  return null;
};

// --- EmailForm Component (Unchanged) ---

const EmailForm = ({ isOpen, onClose }) => {
  const form = useRef(null);
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      await emailjs.sendForm(
        import.meta.env?.VITE_EMAILJS_SERVICE_ID,
        import.meta.env?.VITE_EMAILJS_TEMPLATE_ID,
        form.current,
        {
          publicKey: import.meta.env?.VITE_EMAILJS_API_KEY,
        }
      );
      toast.success('Email Sent ✅');
    } catch (error) {
      console.error(error);
      toast.error('Failed to send email.');
    } finally {
      setLoading(false);
      setFormData({ name: '', email: '', message: '' });
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name || '',
        email: user.emailId || '',
        message: '',
      });
    }
  }, [isOpen, user]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center justify-between">
              <span className="flex items-center">
                <Mail className="mr-2 h-5 w-5 text-green-600" />
                Contact Us
              </span>
              <button
                onClick={() => window.open(`https://wa.me/919899784200`)}
                className="w-10 h-10 rounded-full bg-green-100 hover:bg-green-200 flex items-center justify-center transition-colors duration-200 hover:scale-110"
                aria-label="Contact via WhatsApp"
              >
                <img src="/image.webp" alt="whatsapp" className="w-6 h-6" />
              </button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={sendEmail} ref={form} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="name">Name</Label>
            <Input
              type="text"
              id="name"
              name="user_name"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full p-2 border rounded-md focus-visible:ring-green-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="email">Email</Label>
            <Input
              type="email"
              id="email"
              name="user_email"
              placeholder="Your email address"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full p-2 border rounded-md focus-visible:ring-green-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="message">Message</Label>
            <textarea
              id="message"
              name="message"
              rows={3}
              placeholder="How can we help you?"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              className={`block border w-full rounded-md p-2 focus-visible:ring-green-500 ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
              required
            />
            {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
          </div>
          <div className="flex items-center mt-4 gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="px-4 py-2 border rounded-md"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-green-700 hover:bg-green-800 text-white px-4 py-2 rounded-md"
            >
              {loading ? (
                <span className="flex items-center">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...
                </span>
              ) : (
                'Send Message'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CategoriesEdit;
